import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { useApp } from "../context/AppContext";

const WHATSAPP_GREEN = "#25D366";

const FloatingWhatsApp = () => {
    const { settings } = useApp();
    const [open, setOpen] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    // Show greeting bubble after 4s on first load (only once per session)
    useEffect(() => {
        if (sessionStorage.getItem("wa-greeted")) return;
        const t = setTimeout(() => {
            setShowTooltip(true);
            sessionStorage.setItem("wa-greeted", "1");
            setTimeout(() => setShowTooltip(false), 6000);
        }, 4000);
        return () => clearTimeout(t);
    }, []);

    const number = (settings?.whatsapp_number || "").replace(/\D/g, "");
    if (!number) return null;

    const company = settings?.company_name || "Asis Hotels";
    const defaultMsg = `Merhaba ${company}, otelleriniz hakkında bilgi almak istiyorum.`;
    const waLink = `https://wa.me/${number}?text=${encodeURIComponent(defaultMsg)}`;

    return (
        <div className="fixed bottom-5 right-5 z-[9999] flex flex-col items-end gap-3" data-testid="floating-whatsapp">
            {/* Chat preview card */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="card"
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.25 }}
                        className="w-[300px] bg-emerald_surface border border-border_green shadow-2xl overflow-hidden"
                        data-testid="whatsapp-chat-card"
                    >
                        <div className="px-4 py-3 flex items-center justify-between" style={{ background: WHATSAPP_GREEN }}>
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                                    <MessageCircle className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <div className="text-white font-serif text-base leading-tight">{company}</div>
                                    <div className="text-white/85 text-[11px]">Genellikle dakikalar içinde yanıtlar</div>
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="text-white/80 hover:text-white"
                                aria-label="Kapat"
                                data-testid="close-whatsapp-card"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-4 bg-[url('https://i.pinimg.com/originals/97/c0/07/97c00759d3e90d3e44b7656d6c1043f8.png')] bg-cover bg-blend-overlay" style={{ backgroundColor: "rgba(8,44,39,0.85)" }}>
                            <div className="bg-white text-emerald_deep rounded-lg rounded-tl-none px-3 py-2 text-xs leading-relaxed shadow max-w-[85%]">
                                <div className="font-semibold text-[11px] mb-0.5" style={{ color: WHATSAPP_GREEN }}>{company}</div>
                                Merhaba! 👋 Rezervasyon, fiyatlar veya özel tur teklifleri için bizimle WhatsApp üzerinden iletişime geçin. Yardımcı olmaktan mutluluk duyarız.
                            </div>
                        </div>
                        <a
                            href={waLink}
                            target="_blank"
                            rel="noreferrer"
                            className="block text-center text-white text-xs uppercase tracking-[0.15em] py-3 font-semibold hover:opacity-90 transition-opacity"
                            style={{ background: WHATSAPP_GREEN }}
                            data-testid="open-whatsapp-link"
                        >
                            WhatsApp'tan Sohbet Başlat
                        </a>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Tooltip bubble */}
            <AnimatePresence>
                {!open && showTooltip && (
                    <motion.button
                        key="tooltip"
                        onClick={() => { setOpen(true); setShowTooltip(false); }}
                        initial={{ opacity: 0, x: 30, scale: 0.9 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 30, scale: 0.9 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white text-emerald_deep px-4 py-2 text-xs font-semibold shadow-xl rounded-l-full rounded-tr-full max-w-[230px] text-left hover:bg-bone transition-colors"
                        data-testid="whatsapp-tooltip"
                    >
                        <span className="block leading-tight">Yardıma mı ihtiyacınız var? Hemen yazın!</span>
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Pulsing FAB */}
            <button
                onClick={() => setOpen(!open)}
                className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-110"
                style={{ background: WHATSAPP_GREEN }}
                aria-label="WhatsApp ile iletişime geç"
                data-testid="whatsapp-fab"
            >
                <span
                    className="absolute inset-0 rounded-full animate-ping opacity-40"
                    style={{ background: WHATSAPP_GREEN }}
                />
                {/* WhatsApp glyph */}
                <svg viewBox="0 0 32 32" className="w-7 h-7 relative" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.476-.502 2.95-1.382.07-.143.105-.343.105-.516 0-.143-.07-.286-.15-.4-.16-.18-.488-.279-.85-.443zM16.05 27.99c-1.86 0-3.643-.5-5.213-1.408l-.36-.214-3.762.99 1.003-3.673-.236-.379a10.404 10.404 0 0 1-1.62-5.566c0-5.751 4.677-10.428 10.428-10.428 5.751 0 10.428 4.677 10.428 10.428 0 5.751-4.677 10.428-10.428 10.428z M16.05 4.018c-7.025 0-12.752 5.728-12.752 12.752 0 2.255.6 4.466 1.74 6.4l-1.844 6.738 6.91-1.818a12.71 12.71 0 0 0 5.946 1.516h.005c7.025 0 12.755-5.728 12.755-12.752 0-3.41-1.327-6.616-3.738-9.026A12.624 12.624 0 0 0 16.05 4.018z"/>
                </svg>
            </button>
        </div>
    );
};

export default FloatingWhatsApp;
